import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState('');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [appMetadata, setAppMetadata] = useState(null);

  // Password requirements state
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token || token.length !== 64) {
        setError('Invalid reset link. Please request a new password reset.');
        setVerifying(false);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post(`${BACKEND_URL}/api/auth/verify-reset-token`, {
          token: token
        });

        if (response.data.valid) {
          setTokenValid(true);
          setTokenInfo(response.data);
        } else {
          setError(response.data.message || 'This link has expired or has already been used.');
        }
      } catch (err) {
        setError('This link has expired or has already been used. Please request a new password reset.');
      } finally {
        setVerifying(false);
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  // Fetch app metadata (store URLs + support email) for the success page.
  // This is the same data shown in the super-admin App Metadata panel, so it
  // always stays in sync with whatever's configured there. We swallow errors
  // and fall back to sensible defaults if the call fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/app/metadata`);
        if (!cancelled) setAppMetadata(resp.data || null);
      } catch (_) {
        if (!cancelled) setAppMetadata(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Check password requirements
  useEffect(() => {
    setRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)
    });
  }, [password]);

  const allRequirementsMet = Object.values(requirements).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRequirementsMet) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    // Check terms acceptance for new user setup
    if (tokenInfo?.token_type === 'setup' && !termsAccepted) {
      setError('Please accept the Terms of Service and Privacy Policy to continue');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token: token,
        new_password: password
      });

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Link Expired or Invalid</h1>
          
          <p className="text-gray-600 mb-6">{error}</p>
          
          <div className="space-y-3">
            <Link
              to="/forgot-password"
              className="block w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all font-medium text-center"
            >
              Request New Reset Link
            </Link>
            
            <Link
              to="/admin/login"
              className="block w-full py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium text-center"
            >
              Return to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    // ----------------------------------------------------------------
    // Dynamic success page — 4 variants driven by token_type x user_type.
    // Credentials (org id + username) are echoed back for `setup` only;
    // reset variants intentionally don't re-print them (the user already
    // knows who they are).
    // ----------------------------------------------------------------
    const isSetup = tokenInfo?.token_type === 'setup';
    const isAdmin = tokenInfo?.user_type === 'admin';
    const orgId = tokenInfo?.organisation_id;
    const username = tokenInfo?.username;

    const SUPPORT_EMAIL = appMetadata?.support_email || 'support@webmail.siteorbit.app';
    const IOS_URL = appMetadata?.update_url_ios;
    const ANDROID_URL = appMetadata?.update_url_android;

    const headline = isSetup ? 'Password set successfully!' : 'Password reset successfully!';

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8" data-testid="password-success-card">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-9 h-9 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3" data-testid="password-success-headline">
              {headline}
            </h1>
          </div>

          {/* Body copy — varies by variant */}
          <div className="text-gray-700 text-sm leading-relaxed mb-6">
            {isSetup && isAdmin && (
              <p>Your account is now active. You can sign in using these credentials:</p>
            )}
            {isSetup && !isAdmin && (
              <>
                <p className="mb-2">Welcome to SiteOrbit. Your account is now active.</p>
                <p>You can sign in to the SiteOrbit mobile app using these credentials:</p>
              </>
            )}
            {!isSetup && (
              <>
                <p className="mb-2">You can now sign back in with your new password.</p>
                <p>Use the same Organisation ID and username as before, along with the password you just set.</p>
              </>
            )}
          </div>

          {/* Credentials box (setup only) */}
          {isSetup && (orgId || username) && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-2 text-sm" data-testid="credentials-box">
              {orgId && (
                <div className="flex items-baseline gap-2 mb-1.5">
                  <span className="text-gray-500 w-32 flex-shrink-0">Organisation ID:</span>
                  <span className="font-mono font-medium text-gray-900 break-all">{orgId}</span>
                </div>
              )}
              {username && (
                <div className="flex items-baseline gap-2 mb-1.5">
                  <span className="text-gray-500 w-32 flex-shrink-0">Username:</span>
                  <span className="font-mono font-medium text-gray-900 break-all">{username}</span>
                </div>
              )}
              <div className="flex items-baseline gap-2">
                <span className="text-gray-500 w-32 flex-shrink-0">Password:</span>
                <span className="text-gray-700 italic">The one you just set</span>
              </div>
            </div>
          )}
          {isSetup && (
            <p className="text-xs text-gray-500 italic mb-6">
              Keep your Organisation ID safe — you'll need it every time you log in.
            </p>
          )}

          {/* Primary call-to-action: admin web login (admins only) */}
          {isAdmin && (
            <div className="mb-5">
              <p className="text-sm font-semibold text-gray-900 mb-2">Sign in to the admin web portal</p>
              <Link
                to="/admin/login"
                className="block w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors text-center"
                data-testid="admin-login-cta"
              >
                Go to Admin Login →
              </Link>
            </div>
          )}

          {/* Mobile app store links */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-900 mb-2">
              {isAdmin
                ? 'Or use the mobile app'
                : (isSetup ? 'Download the mobile app' : 'Open the mobile app')}
            </p>
            <div className="flex gap-3">
              {IOS_URL && (
                <a
                  href={IOS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
                  data-testid="ios-store-link"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  App Store
                </a>
              )}
              {ANDROID_URL && (
                <a
                  href={ANDROID_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
                  data-testid="android-store-link"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893 2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198 2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.205 12l2.493-2.491zM5.864 2.658 16.802 8.99l-2.302 2.302-8.636-8.634z"/>
                  </svg>
                  Google Play
                </a>
              )}
            </div>
          </div>

          {/* Support footer — varies by variant */}
          <div className="pt-4 border-t border-gray-100 text-xs text-gray-500 text-center">
            {isSetup && isAdmin && (
              <>Need help? Contact support at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-600 hover:underline">{SUPPORT_EMAIL}</a>
              </>
            )}
            {isSetup && !isAdmin && (
              <>Need help? Ask your organisation admin or email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-600 hover:underline">{SUPPORT_EMAIL}</a>
              </>
            )}
            {!isSetup && isAdmin && (
              <>Forgot your Organisation ID or username? Contact your account administrator or{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-600 hover:underline">{SUPPORT_EMAIL}</a>
              </>
            )}
            {!isSetup && !isAdmin && (
              <>Forgot your Organisation ID or username? Ask your organisation admin or email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-600 hover:underline">{SUPPORT_EMAIL}</a>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Password form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">
            {tokenInfo?.token_type === 'setup' ? 'Set Your Password' : 'Reset Your Password'}
          </h1>
          <p className="text-gray-600 mt-2">
            {tokenInfo?.email && (
              <span>For: <strong>{tokenInfo.email}</strong></span>
            )}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* New Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-200 pr-12"
                placeholder="Enter new password"
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Password Requirements:</p>
            <div className="space-y-2">
              {[
                { key: 'length', label: 'At least 8 characters' },
                { key: 'uppercase', label: 'At least one uppercase letter (A-Z)' },
                { key: 'lowercase', label: 'At least one lowercase letter (a-z)' },
                { key: 'number', label: 'At least one number (0-9)' },
                { key: 'special', label: 'At least one special character (!@#$%^&*...)' }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  {requirements[key] ? (
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className={`text-sm ${requirements[key] ? 'text-green-700' : 'text-gray-600'}`}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all pr-12 ${
                  confirmPassword && !passwordsMatch ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
                placeholder="Confirm new password"
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-red-500 text-sm mt-2">Passwords do not match</p>
            )}
            {passwordsMatch && (
              <p className="text-green-600 text-sm mt-2 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Passwords match
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Terms & Conditions Acceptance - Only for new user setup */}
          {tokenInfo?.token_type === 'setup' && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                  disabled={submitting}
                />
                <label htmlFor="termsAccepted" className="text-sm text-gray-700 cursor-pointer">
                  I have read and agree to the{' '}
                  <a
                    href="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 underline font-medium"
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 underline font-medium"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || !allRequirementsMet || !passwordsMatch || (tokenInfo?.token_type === 'setup' && !termsAccepted)}
            className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {tokenInfo?.token_type === 'setup' ? 'Setting Password...' : 'Resetting Password...'}
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {tokenInfo?.token_type === 'setup' ? 'Set Password' : 'Reset Password'}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
